import styled from '@emotion/styled'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Breakpoint from '../../Breakpoint'
import TrainingSessionGroupComponent from '../../components/coach/TrainingSessionGroup'

import Loader from '../../components/global/Loader'
import SearchInput from '../../components/global/SearchInput'
import Title from '../../components/global/Title'
import UserContext from '../../context/UserContext'
import { useVerifyCoachToken } from '../../hooks/useVerifyCoachToken'
import apiClient from '../../services/apiClient'
import { Intensity } from '../../types/exercise.type'
import type { TrainingSessionGroup as TrainingSessionGroupType } from '../../types/training-session-group.type'

interface TrainingSessionGroupWithSessions extends Omit<TrainingSessionGroupType, 'sessions'> {
    sessions: TrainingSession[]
}

import CoachTrainingSessionFormModal from './CoachTrainingSessionFormModal'

const StyledButton = styled(Button)`
    font-size: 1.6rem;

    &:active {
        transform: scale(0.95);
    }
`

const Btn = styled(StyledButton)`
    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin-right: 0;
        width: 100%;
    }
`

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    margin-right: 8px;
`

const ToggleLabel = styled.span`
    font-size: 1.6rem;
    color: #495057;
`

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
    margin: 0;

    input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #e9ecef;
        transition: 0.4s;
        border-radius: 24px;
    }

    .slider:before {
        position: absolute;
        content: '';
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.4s;
        border-radius: 50%;
    }

    input:checked + .slider {
        background-color: #0d6efd;
    }

    input:checked + .slider:before {
        transform: translateX(26px);
    }

    input:focus + .slider {
        box-shadow: 0 0 1px #0d6efd;
    }
`

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    width: 100%;

    @media (max-width: 750px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`

const ActionsWrapper = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        flex-direction: column;
        gap: 0;
        width: 100%;
    }
`

const ContentWrapper = styled.div`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 24px;
    width: 100%;
    margin-top: 24px;
    padding-bottom: 8px;
    cursor: grab;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    scrollbar-width: thin;
    scrollbar-color: #888 transparent;
    -ms-overflow-style: -ms-autohiding-scrollbar;
    user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;

    &::-webkit-scrollbar {
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #888;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: #666;
    }

    &.grabbing {
        cursor: grabbing;
        user-select: none;
        -webkit-user-drag: none;

        & * {
            pointer-events: none;
        }
    }
`

export interface TrainingSessionStep {
    id?: number
    title: string
    description?: string
    startTime: string
    endTime: string
    exerciseId?: number
}

export interface TrainingSession {
    id?: number
    title: string
    description?: string
    intensity: Intensity
    order: number
    coachId: number
    groupId?: number
    group?: TrainingSessionGroupType
    steps: TrainingSessionStep[]
    isCompleted?: boolean
    completedAt?: string
    createdAt?: string
    updatedAt?: string
}

const CoachTrainingSession: React.FC = () => {
    const contentWrapperRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    const startDragging = (e: React.MouseEvent) => {
        if (!contentWrapperRef.current) return

        setIsDragging(true)
        contentWrapperRef.current.classList.add('grabbing')
        setStartX(e.pageX - contentWrapperRef.current.offsetLeft)
        setScrollLeft(contentWrapperRef.current.scrollLeft)
    }

    const stopDragging = () => {
        if (!contentWrapperRef.current) return

        setIsDragging(false)
        contentWrapperRef.current.classList.remove('grabbing')
    }

    const onDrag = (e: React.MouseEvent) => {
        if (!isDragging || !contentWrapperRef.current) return
        e.preventDefault()

        const x = e.pageX - contentWrapperRef.current.offsetLeft
        const walk = (x - startX) * 2 // Multiplier pour un défilement plus rapide
        contentWrapperRef.current.scrollLeft = scrollLeft - walk
    }

    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const user = useContext(UserContext)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedIntensity] = useState<string>('all')
    const [showCompleted, setShowCompleted] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingSession, setEditingSession] = useState<TrainingSession | null>(null)

    // Vérification du token coach
    const verifyToken = useVerifyCoachToken()

    // Récupération des groupes de sessions avec les sessions associées
    const {
        data: groupsWithSessions = [],
        isLoading,
        refetch: refetchGroups,
    } = useQuery<TrainingSessionGroupWithSessions[]>({
        queryKey: ['trainingSessionGroups', { showCompleted }],
        queryFn: async ({ queryKey }): Promise<TrainingSessionGroupWithSessions[]> => {
            const [_key, { showCompleted }] = queryKey as [string, { showCompleted: boolean }]
            verifyToken()
            if (!user) throw new Error('User not authenticated')

            const response = await apiClient.get<Array<TrainingSessionGroupType & { sessions?: TrainingSession[] }>>(
                '/training-session-groups',
                {
                    params: {
                        isCompleted: showCompleted ? '' : 'false',
                    },
                },
            )

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to fetch training session groups')
            }

            // S'assurer que chaque groupe a une propriété sessions qui est un tableau
            return response.data.map((group) => ({
                ...group,
                sessions: group.sessions || [],
            })) as TrainingSessionGroupWithSessions[]
        },
        enabled: !!user?.coachData?.id,
    })

    // Rafraîchir les données quand le filtre change
    React.useEffect(() => {
        refetchGroups()
    }, [showCompleted, refetchGroups])

    // Filtrage des groupes et sessions en fonction des critères de recherche
    const filteredGroups = useMemo(() => {
        return (
            (groupsWithSessions || [])
                .map((group) => ({
                    ...group,
                    sessions: group.sessions || [], // Assure que sessions est toujours un tableau, même si undefined
                }))
                .filter((group) => {
                    // Filtre par terme de recherche
                    const matchesSearch =
                        !searchTerm ||
                        group.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
                        group.sessions.some(
                            (session) =>
                                session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                (session.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false),
                        )

                    // Filtre par intensité
                    const matchesIntensity =
                        selectedIntensity === 'all' ||
                        group.sessions.some((session) => session.intensity === selectedIntensity)

                    // Filtre par statut de complétion - si showCompleted est false, on ne garde que les groupes qui ont des sessions non terminées
                    const matchesCompletion = showCompleted
                        ? true
                        : group.sessions.some((session) => !session.isCompleted)

                    return matchesSearch && matchesIntensity && matchesCompletion
                })
                // Filtrer les sessions dans chaque groupe en fonction de showCompleted
                .map((group) => ({
                    ...group,
                    sessions: showCompleted ? group.sessions : group.sessions.filter((session) => !session.isCompleted),
                }))
                .filter((group) => group.sessions.length > 0)
        )
    }, [groupsWithSessions, searchTerm, selectedIntensity, showCompleted])

    // Mutation pour supprimer une session
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            verifyToken()

            await apiClient.delete(`/training-sessions/${id}`)

            refetchGroups()
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trainingSessions'] })
            toast.success(t('coach.trainingSessions.deleteSuccess'))
        },
        onError: () => {
            toast.error(t('coach.trainingSessions.deleteError'))
        },
    })

    const toggleComplete = useCallback(
        async (session: TrainingSession) => {
            try {
                verifyToken()
                if (!user) throw new Error('User not authenticated')
                if (!session.id) throw new Error('Session ID is required')

                const isCompleted = !session.isCompleted
                const completedAt = isCompleted ? new Date().toISOString() : null

                const response = await apiClient.patch(`/training-sessions/${session.id}`, { isCompleted, completedAt })
                refetchGroups()

                if (response.status >= 400) {
                    const errorData = response.data as { message?: string }
                    throw new Error(errorData?.message ?? 'Failed to update training session')
                }

                toast.success(t('coach.trainingSessions.updateSuccess'))
                queryClient.invalidateQueries({ queryKey: ['trainingSessionGroups'] })
                return response.data
            } catch (error) {
                console.error('Error updating training session status:', error)
                const errorMessage = error instanceof Error ? error.message : t('coach.trainingSessions.updateError')
                toast.error(errorMessage)
                throw error
            }
        },
        [user, t, queryClient, verifyToken],
    )

    const handleEdit = (session: TrainingSession): void => {
        setEditingSession(session)
        setIsModalOpen(true)
    }

    const handleDelete = (id: number): void => {
        if (window.confirm(t('common.areYouSure'))) {
            deleteMutation.mutate(id)
        }
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditingSession(null)
    }

    const handleCreateNew = () => {
        setEditingSession(null)
        setIsModalOpen(true)
    }

    if (isLoading) {
        return <Loader />
    }

    return (
        <div>
            <TitleWrapper>
                <Title>{t('coach.trainingSessions.title')}</Title>
                <ActionsWrapper>
                    <SearchInputCustom
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('common.search')}
                    />
                    <div
                        style={
                            window.innerWidth <= +Breakpoint.TABLET_L.split('px')[0]
                                ? { display: 'flex', gap: '8px', flexDirection: 'column', width: '100%' }
                                : { display: 'flex', gap: '8px' }
                        }
                    >
                        <ToggleContainer>
                            <ToggleLabel>{t('common.showCompleted')}</ToggleLabel>
                            <ToggleSwitch>
                                <input
                                    type="checkbox"
                                    checked={showCompleted}
                                    onChange={() => setShowCompleted(!showCompleted)}
                                />
                                <span className="slider round"></span>
                            </ToggleSwitch>
                        </ToggleContainer>
                        <Btn variant="primary" onClick={handleCreateNew}>
                            {t('common.create')}
                        </Btn>
                    </div>
                </ActionsWrapper>
            </TitleWrapper>

            <ContentWrapper
                ref={contentWrapperRef}
                onMouseDown={(e) => {
                    // Ne déclencher le défilement que si on clique sur le conteneur lui-même et pas sur un enfant
                    if (e.target === e.currentTarget) {
                        startDragging(e)
                    }
                }}
                onMouseLeave={stopDragging}
                onMouseUp={stopDragging}
                onMouseMove={(e) => {
                    // Ne pas gérer le défilement si on est sur un élément enfant qui gère son propre défilement
                    if (e.target !== e.currentTarget && (e.target as HTMLElement).closest('[data-scrollable]')) {
                        return
                    }
                    onDrag(e)
                }}
            >
                {filteredGroups.length === 0 ? (
                    <p>{t('coach.trainingSessions.noSessions')}</p>
                ) : (
                    filteredGroups.map((group) => (
                        <TrainingSessionGroupComponent
                            key={group.id}
                            group={group}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onToggleComplete={toggleComplete}
                        />
                    ))
                )}
            </ContentWrapper>

            <CoachTrainingSessionFormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                session={editingSession}
                refetchGroups={refetchGroups}
            />
        </div>
    )
}

const SearchInputCustom = styled(SearchInput)`
    margin: 0;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        margin-bottom: 16px;
    }
`

export default CoachTrainingSession
